// Hand-drawn path generation utilities
// Adds organic, sketchy variations to geometric shapes

export interface HandDrawnOptions {
  roughness?: number; // 0-10, higher = more rough
  bowing?: number; // 0-10, how much lines bow
  seed?: number; // for consistent randomness
}

// Simple seeded random number generator for consistent results
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
}

// Generate a hand-drawn line between two points
export function handDrawnLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  options: HandDrawnOptions = {}
): string {
  const { roughness = 2, bowing = 1, seed = 1 } = options;
  const rng = new SeededRandom(seed);

  // Validate and format coordinates for SVG paths
  const validateCoord = (coord: number): number => {
    if (isNaN(coord) || !isFinite(coord)) return 0;
    return Math.round(coord * 100) / 100; // Round to 2 decimal places
  };

  const validX1 = validateCoord(x1);
  const validY1 = validateCoord(y1);
  const validX2 = validateCoord(x2);
  const validY2 = validateCoord(y2);

  const dx = validX2 - validX1;
  const dy = validY2 - validY1;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length < 4) {
    return `M ${validX1} ${validY1} L ${validX2} ${validY2}`;
  }

  // Number of segments based on line length
  const segments = Math.max(2, Math.floor(length / 20));

  let path = `M ${validX1} ${validY1}`;

  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const x = validX1 + dx * t;
    const y = validY1 + dy * t;

    // Add roughness perpendicular to the line
    const perpX = -dy / length;
    const perpY = dx / length;

    const roughnessAmount = roughness * rng.range(-1, 1);
    const bowingAmount = bowing * Math.sin(Math.PI * t) * rng.range(-1, 1);

    const finalX = validateCoord(x + perpX * (roughnessAmount + bowingAmount));
    const finalY = validateCoord(y + perpY * (roughnessAmount + bowingAmount));

    path += ` L ${finalX} ${finalY}`;
  }

  return path;
}

// Generate a hand-drawn rectangle
export function handDrawnRectangle(
  x: number,
  y: number,
  width: number,
  height: number,
  options: HandDrawnOptions = {}
): string {
  const { seed = 1 } = options;
  const rng = new SeededRandom(seed);

  // Create four slightly imperfect lines for the rectangle
  const topLine = handDrawnLine(x, y, x + width, y, {
    ...options,
    seed: rng.next() * 1000,
  });
  const rightLine = handDrawnLine(x + width, y, x + width, y + height, {
    ...options,
    seed: rng.next() * 1000,
  });
  const bottomLine = handDrawnLine(x + width, y + height, x, y + height, {
    ...options,
    seed: rng.next() * 1000,
  });
  const leftLine = handDrawnLine(x, y + height, x, y, {
    ...options,
    seed: rng.next() * 1000,
  });

  return `${topLine} ${rightLine.substring(1)} ${bottomLine.substring(1)} ${leftLine.substring(1)}`;
}

// Generate a hand-drawn rounded rectangle
export function handDrawnRoundedRectangle(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number = 8,
  options: HandDrawnOptions = {}
): string {
  const { roughness = 1.5, bowing: _bowing = 1, seed = 1 } = options;
  const rng = new SeededRandom(seed);

  // Keep radius reasonable
  const r = Math.min(radius, Math.min(width, height) / 4);

  // Start at top-left corner (after radius)
  let path = `M ${x + r} ${y}`;

  // Top line (with roughness)
  const topEndX = x + width - r;
  for (let i = 1; i <= 3; i++) {
    const t = i / 3;
    const px = x + r + (topEndX - x - r) * t;
    const py = y + roughness * rng.range(-1, 1);
    path += ` L ${px} ${py}`;
  }

  // Top-right corner
  path += ` Q ${x + width + roughness * rng.range(-1, 1)} ${y + roughness * rng.range(-1, 1)} ${x + width} ${y + r}`;

  // Right line
  const rightEndY = y + height - r;
  for (let i = 1; i <= 3; i++) {
    const t = i / 3;
    const px = x + width + roughness * rng.range(-1, 1);
    const py = y + r + (rightEndY - y - r) * t;
    path += ` L ${px} ${py}`;
  }

  // Bottom-right corner
  path += ` Q ${x + width + roughness * rng.range(-1, 1)} ${y + height + roughness * rng.range(-1, 1)} ${x + width - r} ${y + height}`;

  // Bottom line
  const bottomEndX = x + r;
  for (let i = 1; i <= 3; i++) {
    const t = i / 3;
    const px = x + width - r - (x + width - r - bottomEndX) * t;
    const py = y + height + roughness * rng.range(-1, 1);
    path += ` L ${px} ${py}`;
  }

  // Bottom-left corner
  path += ` Q ${x + roughness * rng.range(-1, 1)} ${y + height + roughness * rng.range(-1, 1)} ${x} ${y + height - r}`;

  // Left line
  const leftEndY = y + r;
  for (let i = 1; i <= 3; i++) {
    const t = i / 3;
    const px = x + roughness * rng.range(-1, 1);
    const py = y + height - r - (y + height - r - leftEndY) * t;
    path += ` L ${px} ${py}`;
  }

  // Top-left corner
  path += ` Q ${x + roughness * rng.range(-1, 1)} ${y + roughness * rng.range(-1, 1)} ${x + r} ${y}`;

  return path + ' Z';
}

// Generate a hand-drawn ellipse
export function handDrawnEllipse(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  options: HandDrawnOptions = {}
): string {
  const { roughness = 2, seed = 1 } = options;
  const rng = new SeededRandom(seed);

  // Validate and format coordinates for SVG paths
  const validateCoord = (coord: number): number => {
    if (isNaN(coord) || !isFinite(coord)) return 0;
    return Math.round(coord * 100) / 100; // Round to 2 decimal places
  };

  // Use ellipse approximation with cubic Bezier curves
  // This creates a much more accurate ellipse shape
  const kappa = 0.5522848; // Magic number for circle approximation with cubic bezier
  const ox = rx * kappa; // control point offset x
  const oy = ry * kappa; // control point offset y

  // Add some hand-drawn variation
  const roughnessScale = roughness * 0.5;

  // Calculate key points with some randomness
  const top = {
    x: validateCoord(cx + roughnessScale * rng.range(-1, 1)),
    y: validateCoord(cy - ry + roughnessScale * rng.range(-1, 1)),
  };
  const right = {
    x: validateCoord(cx + rx + roughnessScale * rng.range(-1, 1)),
    y: validateCoord(cy + roughnessScale * rng.range(-1, 1)),
  };
  const bottom = {
    x: validateCoord(cx + roughnessScale * rng.range(-1, 1)),
    y: validateCoord(cy + ry + roughnessScale * rng.range(-1, 1)),
  };
  const left = {
    x: validateCoord(cx - rx + roughnessScale * rng.range(-1, 1)),
    y: validateCoord(cy + roughnessScale * rng.range(-1, 1)),
  };

  // Build the ellipse using cubic Bezier curves
  let path = `M ${top.x} ${top.y}`;

  // Top to right
  path += ` C ${top.x + ox} ${top.y} ${right.x} ${right.y - oy} ${right.x} ${right.y}`;

  // Right to bottom
  path += ` C ${right.x} ${right.y + oy} ${bottom.x + ox} ${bottom.y} ${bottom.x} ${bottom.y}`;

  // Bottom to left
  path += ` C ${bottom.x - ox} ${bottom.y} ${left.x} ${left.y + oy} ${left.x} ${left.y}`;

  // Left to top
  path += ` C ${left.x} ${left.y - oy} ${top.x - ox} ${top.y} ${top.x} ${top.y}`;

  return path + ' Z';
}

// Generate a hand-drawn diamond
export function handDrawnDiamond(
  x: number,
  y: number,
  width: number,
  height: number,
  options: HandDrawnOptions = {}
): string {
  const { seed = 1 } = options;
  const rng = new SeededRandom(seed);

  const cx = x + width / 2;
  const cy = y + height / 2;

  // Diamond corners
  const top = { x: cx, y: y };
  const right = { x: x + width, y: cy };
  const bottom = { x: cx, y: y + height };
  const left = { x: x, y: cy };

  // Create four lines for the diamond
  const topRight = handDrawnLine(top.x, top.y, right.x, right.y, {
    ...options,
    seed: rng.next() * 1000,
  });
  const rightBottom = handDrawnLine(right.x, right.y, bottom.x, bottom.y, {
    ...options,
    seed: rng.next() * 1000,
  });
  const bottomLeft = handDrawnLine(bottom.x, bottom.y, left.x, left.y, {
    ...options,
    seed: rng.next() * 1000,
  });
  const leftTop = handDrawnLine(left.x, left.y, top.x, top.y, {
    ...options,
    seed: rng.next() * 1000,
  });

  return `${topRight} ${rightBottom.substring(1)} ${bottomLeft.substring(1)} ${leftTop.substring(1)}`;
}

// Generate a hand-drawn rounded diamond
export function handDrawnRoundedDiamond(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number = 8,
  options: HandDrawnOptions = {}
): string {
  const { roughness = 1.3, bowing: _bowing = 1, seed = 1 } = options;
  const rng = new SeededRandom(seed);

  const cx = x + width / 2;
  const cy = y + height / 2;

  // Make radius more prominent for diamonds
  const r = Math.min(radius * 2, Math.min(width, height) / 4);

  // Diamond corner points
  const top = { x: cx, y: y };
  const right = { x: x + width, y: cy };
  const bottom = { x: cx, y: y + height };
  const left = { x: x, y: cy };

  // Calculate offset points for rounded corners
  const topOffset = r * 0.8;
  const sideOffset = r * 0.6;

  // Start from top-right of top corner
  let path = `M ${cx + topOffset} ${y + topOffset}`;

  // Top-right edge (from top corner to right corner)
  const tr1X = cx + topOffset + (right.x - sideOffset - cx - topOffset) * 0.33;
  const tr1Y = y + topOffset + (right.y - sideOffset - y - topOffset) * 0.33;
  const tr2X = cx + topOffset + (right.x - sideOffset - cx - topOffset) * 0.66;
  const tr2Y = y + topOffset + (right.y - sideOffset - y - topOffset) * 0.66;

  path += ` L ${tr1X + roughness * rng.range(-1, 1)} ${tr1Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${tr2X + roughness * rng.range(-1, 1)} ${tr2Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${right.x - sideOffset} ${right.y - sideOffset}`;

  // Right corner (rounded)
  path += ` Q ${right.x + roughness * rng.range(-2, 2)} ${right.y + roughness * rng.range(-2, 2)} ${right.x - sideOffset} ${right.y + sideOffset}`;

  // Right-bottom edge (from right corner to bottom corner)
  const rb1X =
    right.x - sideOffset + (bottom.x + topOffset - right.x + sideOffset) * 0.33;
  const rb1Y =
    right.y + sideOffset + (bottom.y - topOffset - right.y - sideOffset) * 0.33;
  const rb2X =
    right.x - sideOffset + (bottom.x + topOffset - right.x + sideOffset) * 0.66;
  const rb2Y =
    right.y + sideOffset + (bottom.y - topOffset - right.y - sideOffset) * 0.66;

  path += ` L ${rb1X + roughness * rng.range(-1, 1)} ${rb1Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${rb2X + roughness * rng.range(-1, 1)} ${rb2Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${bottom.x + topOffset} ${bottom.y - topOffset}`;

  // Bottom corner (rounded)
  path += ` Q ${bottom.x + roughness * rng.range(-2, 2)} ${bottom.y + roughness * rng.range(-2, 2)} ${bottom.x - topOffset} ${bottom.y - topOffset}`;

  // Bottom-left edge (from bottom corner to left corner)
  const bl1X =
    bottom.x - topOffset + (left.x + sideOffset - bottom.x + topOffset) * 0.33;
  const bl1Y =
    bottom.y - topOffset + (left.y + sideOffset - bottom.y + topOffset) * 0.33;
  const bl2X =
    bottom.x - topOffset + (left.x + sideOffset - bottom.x + topOffset) * 0.66;
  const bl2Y =
    bottom.y - topOffset + (left.y + sideOffset - bottom.y + topOffset) * 0.66;

  path += ` L ${bl1X + roughness * rng.range(-1, 1)} ${bl1Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${bl2X + roughness * rng.range(-1, 1)} ${bl2Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${left.x + sideOffset} ${left.y + sideOffset}`;

  // Left corner (rounded)
  path += ` Q ${left.x + roughness * rng.range(-2, 2)} ${left.y + roughness * rng.range(-2, 2)} ${left.x + sideOffset} ${left.y - sideOffset}`;

  // Left-top edge (from left corner back to top corner)
  const lt1X =
    left.x + sideOffset + (top.x - topOffset - left.x - sideOffset) * 0.33;
  const lt1Y =
    left.y - sideOffset + (top.y + topOffset - left.y + sideOffset) * 0.33;
  const lt2X =
    left.x + sideOffset + (top.x - topOffset - left.x - sideOffset) * 0.66;
  const lt2Y =
    left.y - sideOffset + (top.y + topOffset - left.y + sideOffset) * 0.66;

  path += ` L ${lt1X + roughness * rng.range(-1, 1)} ${lt1Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${lt2X + roughness * rng.range(-1, 1)} ${lt2Y + roughness * rng.range(-1, 1)}`;
  path += ` L ${top.x - topOffset} ${top.y + topOffset}`;

  // Top corner (rounded) - back to start
  path += ` Q ${top.x + roughness * rng.range(-2, 2)} ${top.y + roughness * rng.range(-2, 2)} ${top.x + topOffset} ${top.y + topOffset}`;

  return path + ' Z';
}

// Generate a hand-drawn arrow
export function handDrawnArrow(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  options: HandDrawnOptions = {}
): { line: string; arrowhead: string } {
  const { seed = 1 } = options;
  const rng = new SeededRandom(seed);

  // Main line
  const line = handDrawnLine(x1, y1, x2, y2, {
    ...options,
    seed: rng.next() * 1000,
  });

  // Arrowhead
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const arrowLength = 15;
  const arrowAngle = Math.PI / 6;

  const arrowX1 = x2 - arrowLength * Math.cos(angle - arrowAngle);
  const arrowY1 = y2 - arrowLength * Math.sin(angle - arrowAngle);
  const arrowX2 = x2 - arrowLength * Math.cos(angle + arrowAngle);
  const arrowY2 = y2 - arrowLength * Math.sin(angle + arrowAngle);

  const arrowLine1 = handDrawnLine(x2, y2, arrowX1, arrowY1, {
    ...options,
    seed: rng.next() * 1000,
  });
  const arrowLine2 = handDrawnLine(x2, y2, arrowX2, arrowY2, {
    ...options,
    seed: rng.next() * 1000,
  });

  return {
    line,
    arrowhead: `${arrowLine1} ${arrowLine2}`,
  };
}

// Generate consistent seed from element ID
export function generateSeed(elementId: string | undefined): number {
  if (!elementId || typeof elementId !== 'string') {
    // Fallback to a random seed if elementId is undefined or invalid
    return Math.floor(Math.random() * 1000000);
  }

  let hash = 0;
  for (let i = 0; i < elementId.length; i++) {
    const char = elementId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}
